/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, effect, Inject, input, Input } from '@angular/core';
import { RouterModule } from '@angular/router';
import { TooltipModule } from 'primeng/tooltip';

import { ENVIRONMENT, Environment, EnvironmentService, MoreInfoFields, MoreInfoParams } from '@environment';
import { MovieLoadingComponent, MovieDetailSkeletonComponent } from '@usheru-hff/ui/movie-detail-page';
import { isTBCDate } from '@usheru-hff/sdk/utils';
import {
  Channel,
  Company,
  Country,
  Language,
  Movie,
  movieCountries,
  movieRatingTranslations
} from '@usheru-hff/shared/domain';
import { Genre } from '@usheru-hff/shared/domain/genre';
import { getDateFromDDMMYYYY, SharedUtilsModule } from '@usheru-hff/shared/utils';
import { ImageService } from '@usheru-hff/shared/utils-image';
import { TranslationsService } from '@usheru-hff/shared/utils-translation';

interface ListItem {
  slug: string;
  tr: string;
  trMsg?: string;
}

/**
 * Component to display movie data related to movie information.
 *
 * //TODO barbeitoalex propostal - organice life cycle methods. Split in specific method to improve docs.
 * //TODO barbeitoalex propostal - update translate text with an observable to the current language
 */
@Component({
  selector: 'ush-movie-more-information',
  templateUrl: './movie-more-information.component.html',
  styleUrls: ['./movie-more-information.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    SharedUtilsModule,
    TooltipModule,
    RouterModule,
    NgOptimizedImage,
    MovieDetailSkeletonComponent
  ]
})
export class MovieMoreInformationComponent implements MovieLoadingComponent {
  /**
   * @docNotNeccesary
   */

  readonly movie = input<Movie | null>(null);
  readonly isLoadingData = input(false);
  readonly hideTitle = input(false);

  /**
   * @docNotNeccesary
   */
  @Input()
  channel?: Channel;

  /**
   * @docNotNeccesary
   */
  @Input()
  showPortrait = true;

  /**
   * @docNotNeccesary
   */
  @Input()
  widthPortrait = 300;

  @Input()
  disableImdb = false;

  @Input()
  disableTmdb = false;
  movieRatingTranslations = movieRatingTranslations;

  /**
   * Country names list of the {@link movie}.
   */
  countryList: ListItem[] = [];

  /**
   * Language names list of the {@link movie}.
   */
  languagesList: ListItem[] | undefined;

  /**
   * Genre names list of the {@link movie}.
   */
  genres: Genre[] | undefined;

  /**
   * General release date text.
   */
  releaseDate: string | undefined;

  /**
   * Release date text of the active location ({@link LocationService})
   */
  locReleaseDate: string | undefined;

  /**
   * Streaming Release date text ({@link LocationService})
   */
  streamingReleaseDate: string | undefined;

  /**
   * loc Streaming Release date text ({@link LocationService})
   */
  locStreamingReleaseDate: string | undefined;

  /**
   * Streaming Release date as boolean ({@link LocationService})
   */
  showReleaseDate: boolean;

  /**
   * Streaming Release date as boolean ({@link LocationService})
   */
  showLocReleaseDate: boolean;

  /**
   * Streaming Release date as boolean ({@link LocationService})
   */
  showStreamingDate: boolean;

  /**
   * Streaming Release date as boolean ({@link LocationService})
   */
  showLocStreamingDate: boolean;

  /**
   * Sales agent enabled from env.
   */
  salesAgentsEnabled: boolean;

  /**
   * Used to know what data show or hide. Setted from {@link Environment} moreInfoParams
   */
  infoParams: MoreInfoParams | undefined;

  /**
   * @docNotNeccesary
   */
  rightOwners: Company[] | undefined;

  /**
   * Hide the data under this component header. Setted from {@link Environment} movieInfoCollapse.
   */
  canCollapse = false;

  /**
   * Status used if {@link canCollapse} is enabled.
   */
  collapsed = false;

  /**
   * To show filmafinity link.
   */
  @Input() showTPAffinity: string;

  @Input() titleAsLink: string;

  /**
   * Enables show the {@link rightOwners}. Setted from {@link Environment} movieInfoShowRightOwners.
   */
  showRightOwners = false;

  salesAgentImgError: number[] = [];

  /**
   * Setted from {@link Environment} movieInfoModalPortrait
   */
  movieInfoModalPortrait = true;
  portraitPlaceholder: string;
  tmdbLogoColor: 'black' | 'white';

  enabledRedirections: MoreInfoFields[] = [];
  readonly REDIRECTIONS_BASE = '/movies/all';

  constructor(
    private translationsService: TranslationsService,
    @Inject(ENVIRONMENT) private env: Environment,
    private envService: EnvironmentService,
    private changeDt: ChangeDetectorRef,
    private imageService: ImageService
  ) {
    this.canCollapse = this.env?.customPageParams?.movieInfoCollapse || false;
    this.collapsed = (this.canCollapse && this.env?.customPageParams?.movieInfoCollapseInitial) || false;
    this.tmdbLogoColor = this.env.customPageParams?.movieDetailTMDBLogo || 'black';
    // this.showTPAffinity = this.env?.customPageParams?.movieInfoShowTPAffinity || false;
    this.showRightOwners = this.env?.customPageParams?.movieInfoShowRightOwners || false;
    this.salesAgentsEnabled = env?.enableSalesAgent;
    this.portraitPlaceholder = imageService.portraitPlaceholder;
    // Default is true and the user must set as false in the environemnt
    //TODO could be better know in what apps we need this clcklable with another card
    // this.movieInfoModalPortrait = this.env.customPageParams?.movieInfoModalPortrait || true;
    this.movieInfoModalPortrait =
      this.env?.customPageParams?.movieInfoModalPortrait == undefined
        ? true
        : this.env.customPageParams?.movieInfoModalPortrait;

    effect(() => {
      if (this.movie()) {
        this.setText();
      }
    });
  }

  get countries() {
    const movie = this.movie();
    return movie ? movieCountries(movie) : [];
  }

  setText() {
    const movie = this.movie();
    if (!movie) return;

    this.infoParams = this.env.moreInfoParams ? this.env.moreInfoParams : undefined;
    if (this.env.countriesPipe) this.env.countriesPipe(this.countries);
    this.countryList = this.collect<Country, ListItem>(this.countries, country => ({
      slug: country.slug,
      tr: country.name,
      trMsg: country.trMsg
    }));
    this.languagesList = this.collect<Language, ListItem>(movie.languages, lang => ({
      slug: lang.slug,
      tr: lang.name,
      trMsg: lang.trMsg
    }));
    this.genres = movie.genres;
    this.rightOwners = movie.contractualPartners;
    this.releaseDate = this.getDateLocaleString(getDateFromDDMMYYYY(movie.releaseDate));
    this.locReleaseDate = this.getDateLocaleString(getDateFromDDMMYYYY(movie.locReleaseDate));
    this.streamingReleaseDate = this.getDateLocaleString(getDateFromDDMMYYYY(movie.streamingReleaseDate));
    this.locStreamingReleaseDate = this.getDateLocaleString(getDateFromDDMMYYYY(movie.locStreamingReleaseDate));
    this.showReleaseDate = this.envService.movieMoreInfoComponent('showReleaseDate')(
      this.infoParams as MoreInfoParams,
      movie
    );
    this.showLocReleaseDate = this.envService.movieMoreInfoComponent('showLocReleaseDate')(
      this.infoParams as MoreInfoParams,
      movie
    );
    this.enabledRedirections = this.envService.movieMoreInfoComponent('enabledRedirections')(this.env.appHasOnlyMovies);
    this.showStreamingDate = this.envService.movieMoreInfoComponent('showStreamingDate')(movie);
    this.showLocStreamingDate = this.envService.movieMoreInfoComponent('showLocStreamingDate')(movie);

    this.changeDt.markForCheck();
  }

  /**
   * Map a list of objects.
   * @param arr
   * @param mapper
   * @returns
   */
  private collect<K, T>(arr: K[], mapper: (el: K) => T): T[] {
    if (arr && arr.length) {
      return arr.map(mapper).filter(el => {
        if (Array.isArray(el)) {
          return el.length > 0;
        }
        return el !== null && el !== undefined && el !== '';
      });
    }

    return [];
  }
  onSalesAgentLogoError(index) {
    this.salesAgentImgError.push(index);
  }

  imgWithError(index) {
    return this.salesAgentImgError.includes(index);
  }

  /**
   * Get date string using {@link infoParams} format.
   *
   * //TODO barbeitoalex propostal - change this to able to merge with {@link getLocReleaseDate}
   * @param date
   * @returns
   */
  getDateLocaleString(date: Date) {
    if (!date) return null;
    if (isTBCDate(date)) {
      return $localize`:@@showtimes.day.tbc:TBC`;
    }
    let dateFormat;
    if (this.env.moreInfoParams?.locReleaseDate) {
      switch (this.env.moreInfoParams.locReleaseDate) {
        case 'year':
          dateFormat = {
            year: 'numeric'
          };
          break;
        default:
          dateFormat = {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
          };
          break;
      }
    } else {
      dateFormat = {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      };
    }
    return date.toLocaleDateString(this.translationsService.currentLanguage().code, dateFormat);
  }

  /**
   * @docNotNeccesary
   * @param value
   */
  toggleCollapse(value?: boolean) {
    if (this.canCollapse) this.collapsed = value || !this.collapsed ? true : false;

    // if (!this.collapsed) {
    //TODO Tracking - maybe track once por load?
    // }
  }
}
